import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Box, Grid, Typography } from '@mui/material';
import { FormikValues } from 'formik';

import {
  GA_EVENTS,
  getEmail,
  getEnableAiOnboarding,
  getLanguages,
  getProfileUtils,
  getSignupRequestId,
  getTypeOfUser,
  getUserProps,
  hasErrorsSelector,
  successSelector,
  TypeOfUser,
  userActions,
} from '@pharmaplan/common';

import useProfessional from '../../hooks/Professional/useProfessional';
import useSelfService from '../../hooks/useSelfService';
import useSignupFlow from '../../hooks/useSignupFlow/useSignupFlow';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import useAnalytics from '../../analytics/useAnalytics';
import { setData } from '../../actions/persistActions';
import strings from '../../localization';
import OutcomeModal from '../Modals/OutcomeModal';
import { OutcomeModalTypes } from '../../helpers/Constants';
import { resetDialog, setDialog } from '../../reducers/dialogReducer';

import PharmacistSignupFlow from './PharmacistSignupFlow';
import PharmacySignupFlow from './PharmacySignupFlow';
import SignupStepper from './SignupStepper';
import AiCompanion from './AiCompanion';
import {
  AiCompanionSessionContext,
  ProfessionalType,
} from './AiCompanion/types';
import CompletionMethodChoice from './CompletionMethodChoice';
import styles from './styles';

const {
  pharmacist,
  pharmacy,
  student,
  technician,
} = TypeOfUser;

const professionalTypes: Partial<Record<TypeOfUser, ProfessionalType>> = {
  [pharmacist]: 'pharmacist',
  [student]: 'student',
  [technician]: 'technician',
};

type CompletionMethod = 'choice' | 'ai' | 'manual';

const SignupFlow = () => {
  const dispatch = useAppDispatch();

  const analytics = useAnalytics();

  const { selfUserType } = useProfessional();
  const { isSelfService } = useSelfService();

  const isAiOnboardingEnabled = useAppSelector(getEnableAiOnboarding);

  // AI onboarding is a full-service-only flow, and full service can still
  // turn it off from admin configuration. Either way, fall back to the
  // original manual stepper.
  const offersAiOnboarding = !isSelfService && isAiOnboardingEnabled;

  const [step, setStep] = useState(0);
  const [completionMethod, setCompletionMethod] = useState<CompletionMethod>(
    offersAiOnboarding ? 'choice' : 'manual',
  );

  const userType = useAppSelector(getTypeOfUser);
  const userProps = useAppSelector(getUserProps);
  const email = useAppSelector(getEmail);
  const signupRequestId = useAppSelector(getSignupRequestId);
  const languages = useAppSelector(getLanguages);
  const profileUtilities = useAppSelector(getProfileUtils);
  const isProfileUtilsReady = useAppSelector((state) =>
    successSelector([userActions.profileUtils], state));
  const hasProfileUtilsError = useAppSelector((state) =>
    hasErrorsSelector([userActions.profileUtils], state));
  const {
    contactPreferences = [],
    pharmacistCapacities = [],
    prefferedReplacementDurations = [],
    provinces = [],
    softwares = [],
  } = profileUtilities ?? {};

  const isPharmacy = userType === pharmacy;
  const canUseAiCompanion = !isPharmacy && offersAiOnboarding;

  let titleLabel = strings.professional;

  if (isPharmacy) {
    titleLabel = strings.pharmacy;
  }

  const title = strings.formatString(
    strings.signupAs,
    titleLabel,
  );

  const signupEmail = userProps?.email?.trim() || email?.trim();
  const signupFirstName = userProps?.firstName?.trim() || null;
  const signupLastName = userProps?.lastName?.trim() || null;
  const normalizedSignupRequestId = signupRequestId?.trim() || null;

  const aiCompanionSessionContext = useMemo<
    AiCompanionSessionContext | null
  >(() => {
    const selectedLanguage = languages.find(({ languageId }) =>
      languageId === userProps?.language);
    const professionalType = professionalTypes[userType];

    if (
      !professionalType
      || !selectedLanguage?.name
      || !signupEmail
      || !signupFirstName
      || !signupLastName
      || !userProps?.language
    ) {
      return null;
    }

    return {
      contactPreferenceOptions: contactPreferences.map(
        ({ contactPreferenceId, name }) =>
          ({
            contactPreferenceId,
            name,
          }),
      ),
      email: signupEmail,
      firstName: signupFirstName,
      lastName: signupLastName,
      preferredLanguage: selectedLanguage.name,
      preferredLanguageId: userProps.language,
      preferredReplacementDurationOptions:
        prefferedReplacementDurations,
      professionalCapacityOptions: pharmacistCapacities.map(
        ({ description, pharmacistCapacityId }) =>
          ({
            description,
            professionalCapacityId: pharmacistCapacityId,
          }),
      ),
      professionalType,
      provinceOptions: provinces.map(({ key, name }) =>
        ({
          key,
          name,
        })),
      softwareOptions: softwares.map(({ name, softwareId }) =>
        ({
          name,
          softwareId,
        })),
    };
  }, [
    contactPreferences,
    languages,
    pharmacistCapacities,
    prefferedReplacementDurations,
    provinces,
    signupEmail,
    signupFirstName,
    signupLastName,
    softwares,
    userProps?.language,
    userType,
  ]);
  const hasAiConfigurationError = hasProfileUtilsError
    || (isProfileUtilsReady
      && (!aiCompanionSessionContext || !normalizedSignupRequestId));

  const handleAiFallbackToManual = useCallback(() => {
    setCompletionMethod('manual');
    dispatch(
      setDialog({
        Component: (
          <OutcomeModal
            customClose={() =>
              dispatch(resetDialog())}
            customCloseLabel={strings.fillFormManually}
            message={strings.aiCompanionFallbackMessage}
            type={OutcomeModalTypes.error}
          />
        ),
        showCloseButton: false,
      }),
    );
  }, [dispatch]);

  const handleChooseAi = useCallback(() => {
    setCompletionMethod('ai');
  }, []);

  const handleChooseManual = useCallback(() => {
    setCompletionMethod('manual');
  }, []);

  useEffect(() => {
    if (completionMethod === 'ai' && hasAiConfigurationError) {
      handleAiFallbackToManual();
    }
  }, [completionMethod, handleAiFallbackToManual, hasAiConfigurationError]);

  const changeStep = (steps: object, values: FormikValues, type: string) => {
    // USER MOVED TO NEXT STEP
    analytics?.logCustomEvent({
      event: GA_EVENTS.signupProgress,
      step_name: (steps?.[step as keyof typeof steps] as { screenName: string })
        ?.screenName,
      step_number: step,
    });

    dispatch(
      setData({
        userType: selfUserType,
        type,
        data: { ...values },
        email,
      }),
    );

    if (step < Object.keys(steps).length - 1) {
      setStep(step + 1);
    }

    return null;
  };

  // USER INITIATED SIGNUP
  useEffect(() => {
    analytics?.logCustomEvent({
      event: GA_EVENTS.signup,
      method: userProps?.loginType,
      role: userType,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userProfile = () => {
    if (isPharmacy) {
      return (
        <PharmacySignupFlow
          setStep={setStep}
          step={step}
          changeStep={changeStep}
        />
      );
    }

    return (
      <PharmacistSignupFlow
        setStep={setStep}
        step={step}
        changeStep={changeStep}
      />
    );
  };

  const completionContent = () => {
    if (canUseAiCompanion && completionMethod === 'choice') {
      return (
        <CompletionMethodChoice
          onChooseAi={handleChooseAi}
          onChooseManual={handleChooseManual}
        />
      );
    }

    if (canUseAiCompanion && completionMethod === 'ai') {
      if (hasAiConfigurationError) {
        return (
          <Typography aria-live="polite">
            {strings.aiCompanionConnecting}
          </Typography>
        );
      }

      if (!isProfileUtilsReady) {
        return (
          <Typography aria-live="polite">
            {strings.aiCompanionConnecting}
          </Typography>
        );
      }

      if (!aiCompanionSessionContext || !normalizedSignupRequestId) {
        return null;
      }

      return (
        <AiCompanion
          sessionContext={aiCompanionSessionContext}
          signupRequestId={normalizedSignupRequestId}
          onBack={() =>
            setCompletionMethod('choice')}
          onFallbackToManual={handleAiFallbackToManual}
          onSwitchToManual={handleChooseManual}
        />
      );
    }

    return (
      <>
        <SignupStepper step={step} setStep={setStep} />
        {userProfile()}
      </>
    );
  };

  useSignupFlow();

  return (
    <Box sx={styles.boxContainer}>
      <Grid lg={8} sx={styles.container} container direction="column">
        {completionMethod !== 'ai' ? (
          <Typography color="primary" sx={styles.title}>
            {title}
          </Typography>
        ) : null}

        {completionContent()}
      </Grid>
    </Box>
  );
};

export default SignupFlow;
